<template>
    <!-- サンプル画像モーダル -->
    <div v-if="show" class="sample-image-modal" @click="closeModal">
        <div class="modal-content" @click.stop>
            <!-- 閉じるボタン -->
            <button class="modal-close" @click="closeModal">✕</button>

            <div class="modal-body">
                <!-- 画像表示エリア -->
                <div class="modal-image-container">
                    <webp-picture v-if="imageData.webp_path" :webp="imageData.webp_path" class="inherit">
                        <img :src="imageData.img_file" :alt="imageData.item_title" class="modal-image">
                    </webp-picture>
                    <img v-else :src="imageData.img_file" :alt="imageData.item_title" class="modal-image">
                </div>

                <!-- 商品情報エリア -->
                <div class="modal-info">
                    <h3 class="item-title">{{ imageData.item_title }}</h3>
                    <p class="talent-name">{{ talentNameDisplay }}</p>

                    <!-- ジャンル（属性）タグ -->
                    <div v-if="imageData.genres && imageData.genres.length > 0" class="item-genres">
                        <span v-for="genre in imageData.genres" :key="genre.id" class="genre-tag">
                            {{ genre.name }}
                        </span>
                    </div>

                    <!-- アクションボタン -->
                    <div class="modal-actions">
                        <a :href="imageData.item_url" class="c-round-btn is-positive">
                            商品詳細へ
                        </a>
                        <a
                            v-if="showTalentButton && imageData.talent_url"
                            :href="imageData.talent_url"
                            class="c-round-btn is-sub"
                        >
                            モデル詳細へ
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        /** モーダルの表示/非表示 */
        show: {
            type: Boolean,
            default: false
        },
        /** 表示する画像データ */
        imageData: {
            type: Object,
            default: () => ({})
        },
        /** 「モデル詳細へ」ボタンを表示するか */
        showTalentButton: {
            type: Boolean,
            default: true
        }
    },
    emits: ['close'],
    computed: {
        /**
         * タレント名を表示用にフォーマット
         * @returns {string} タレント名（複数の場合は " / " で連結）
         */
        talentNameDisplay() {
            if (this.imageData.talents && this.imageData.talents.length > 0) {
                return this.imageData.talents.join(' / ');
            }
            return '';
        }
    },
    watch: {
        /**
         * モーダルの表示状態に応じてbodyのスクロールを制御
         */
        show(newVal) {
            if (newVal) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        }
    },
    created() {
        /**
         * Escキーでモーダルを閉じるハンドラー
         */
        this.handleKeydown = (e) => {
            if (this.show && e.key === 'Escape') {
                this.closeModal();
            }
        };
    },
    mounted() {
        window.addEventListener('keydown', this.handleKeydown);
    },
    beforeUnmount() {
        window.removeEventListener('keydown', this.handleKeydown);
    },
    methods: {
        /**
         * モーダルを閉じる
         */
        closeModal() {
            this.$emit('close');
        }
    }
};
</script>

<style scoped>
/* ========================================
   モーダルオーバーレイ
   ======================================== */
.sample-image-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.8);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

/* ========================================
   モーダルコンテンツ
   ======================================== */
.modal-content {
    background-color: white;
    border-radius: 8px;
    max-width: 900px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.modal-body {
    padding: 24px;
}

/* ========================================
   閉じるボタン
   ======================================== */
.modal-close {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 36px;
    height: 36px;
    padding: 0;
    background-color: rgba(0, 0, 0, 0.6);
    border: none;
    border-radius: 50%;
    color: white;
    font-size: 20px;
    line-height: 1.7;
    cursor: pointer;
    z-index: 10;
    transition: background-color 0.2s;
    display: flex;
    justify-content: center;
}

.modal-close:hover {
    background-color: rgba(0, 0, 0, 0.8);
}

/* ========================================
   画像表示エリア
   ======================================== */
.modal-image-container {
    width: 100%;
    max-height: 500px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    background-color: #f5f5f5;
    border-radius: 4px;
}

.modal-image {
    max-width: 100%;
    max-height: 500px;
    object-fit: contain;
}

/* ========================================
   商品情報エリア
   ======================================== */
.modal-info {
    text-align: center;
}

.item-title {
    font-size: 20px;
    font-weight: bold;
    margin-bottom: 12px;
    color: #333;
    line-height: 1.5;
}

.talent-name {
    font-size: 16px;
    color: #666;
    margin-bottom: 16px;
}

/* ========================================
   ジャンルタグ
   ======================================== */
.item-genres {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
    margin-bottom: 24px;
}

.genre-tag {
    display: inline-block;
    padding: 6px 12px;
    background-color: #e9ecef;
    border-radius: 16px;
    font-size: 13px;
    color: #495057;
}

/* ========================================
   アクションボタン
   ======================================== */
.modal-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;
}

.modal-actions a {
    min-width: 180px;
    text-decoration: none;
}

/* ========================================
   レスポンシブ対応（SP）
   ======================================== */
@media (max-width: 767px) {
    .sample-image-modal {
        padding: 10px;
    }

    .modal-content {
        max-height: 95vh;
    }

    .modal-body {
        padding: 16px;
    }

    .modal-close {
        top: 8px;
        right: 8px;
        width: 32px;
        height: 32px;
        font-size: 18px;
    }

    .modal-image-container {
        max-height: 300px;
        margin-bottom: 16px;
    }

    .modal-image {
        max-height: 300px;
    }

    .item-title {
        font-size: 18px;
        margin-bottom: 10px;
    }

    .talent-name {
        font-size: 14px;
        margin-bottom: 12px;
    }

    .item-genres {
        margin-bottom: 20px;
    }

    .genre-tag {
        font-size: 12px;
        padding: 5px 10px;
    }

    .modal-actions {
        flex-direction: column;
        gap: 10px;
    }

    .modal-actions a {
        width: 100%;
        min-width: auto;
    }
}
</style>
